import type {
	AxiosRequestConfig,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from "axios";
import type { ApiRequestOptions } from "./ApiRequestOptions";
import axios from "axios";
import { store } from "../../store";
import { logout } from "../../store/auth/slice";
import { refreshTokenAuthRefreshPost } from "../services.gen";
import { PATHS } from "../../routes/paths";

type Headers = Record<string, string>;
type Middleware<T> = (value: T) => T | Promise<T>;
type Resolver<T> = (options: ApiRequestOptions<T>) => Promise<T>;

export class Interceptors<T> {
	_fns: Middleware<T>[];

	constructor() {
		this._fns = [];
	}

	eject(fn: Middleware<T>): void {
		const index = this._fns.indexOf(fn);
		if (index !== -1) {
			this._fns = [...this._fns.slice(0, index), ...this._fns.slice(index + 1)];
		}
	}

	use(fn: Middleware<T>): void {
		this._fns = [...this._fns, fn];
	}
}

export type OpenAPIConfig = {
	BASE: string;
	CREDENTIALS: "include" | "omit" | "same-origin";
	ENCODE_PATH?: ((path: string) => string) | undefined;
	HEADERS?: Headers | Resolver<Headers> | undefined;
	PASSWORD?: string | Resolver<string> | undefined;
	TOKEN?: string | Resolver<string> | undefined;
	USERNAME?: string | Resolver<string> | undefined;
	VERSION: string;
	WITH_CREDENTIALS: boolean;
	interceptors: {
		request: Interceptors<AxiosRequestConfig>;
		response: Interceptors<AxiosResponse>;
	};
};

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
	_retry?: boolean;
}

let isRefreshing = false;
let failedQueue: Array<{
	resolve: (value?: any) => void;
	reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
	failedQueue.forEach((request) => {
		if (error) {
			request.reject(error);
		} else {
			request.resolve();
		}
	});
	failedQueue = [];
};

// レスポンスの成功ハンドラー
const responseSuccessHandler = (response: AxiosResponse) => {
	return response;
};

// レスポンスのエラーハンドラー
const responseErrorHandler = async (error: any) => {
	const originalRequest = error.config as ExtendedAxiosRequestConfig;

	// ネットワークエラーや403エラーの処理
	if (error.message === 'Network Error' || error.response?.status === 403) {

		const isPrivateRoute = originalRequest.url && !originalRequest.url.includes('/auth/');


		if (isPrivateRoute) {
			store.dispatch(logout());
			window.location.href = PATHS.AUTH_SIGN_IN;
			console.groupEnd();
			return Promise.reject(error);
		}
	}

	// リフレッシュトークン処理のスキップ
	if (originalRequest.url?.includes("refresh")) {
		console.groupEnd();
		return Promise.reject(error);
	}

	// 401エラー処理
	if (error.response?.status === 401 && !originalRequest._retry) {

		if (isRefreshing) {
			return new Promise((resolve, reject) => {
				failedQueue.push({ resolve, reject });
			})
				.then(() => {
					return axios(originalRequest);
				})
				.catch((err) => {
					console.error('Failed to retry request after token refresh:', err);
					store.dispatch(logout());
					window.location.href = PATHS.AUTH_SIGN_IN;
					return Promise.reject(err);
				});
		}

		originalRequest._retry = true;
		isRefreshing = true;
		console.log('Attempting token refresh');

		try {
			await refreshTokenAuthRefreshPost();
			processQueue();
			isRefreshing = false;
			return await axios(originalRequest);
		} catch (refreshError) {
			processQueue(refreshError);
			isRefreshing = false;
			store.dispatch(logout());
			window.location.href = PATHS.AUTH_SIGN_IN;
			console.groupEnd();
			return Promise.reject(refreshError);
		}
	}

	console.groupEnd();
	return Promise.reject(error);
};

export const OpenAPI: OpenAPIConfig = {
	BASE: import.meta.env.VITE_BASE_URL || "http://localhost:8081",
	CREDENTIALS: "include",
	ENCODE_PATH: undefined,
	HEADERS: undefined,
	PASSWORD: undefined,
	TOKEN: undefined,
	USERNAME: undefined,
	VERSION: "0.1.0",
	WITH_CREDENTIALS: true,
	interceptors: {
		request: new Interceptors<AxiosRequestConfig>(),
		response: new Interceptors<AxiosResponse>(),
	},
};

// 成功とエラーの両方のハンドラーを設定
axios.interceptors.response.use(responseSuccessHandler, responseErrorHandler);
